/**
 * In-session Queue notes, decision draft, and Archive handoff.
 * Sample cases stay in their source files. This only records work done in the current session.
 * Moderation does not use Start Case or Support Team assignment actions.
 */

const overrides = new Map();
const decided = new Map();

const keyOf = (caseId) => String(caseId || '').trim();

export function getModerationCaseOverride(caseId) {
  return overrides.get(keyOf(caseId)) || null;
}

export function patchModerationCaseOverride(caseId, patch) {
  const key = keyOf(caseId);
  if (!key) return null;
  const next = { ...(overrides.get(key) || {}), ...patch };
  overrides.set(key, next);
  return next;
}

export function isModerationCaseDecided(caseId) {
  return decided.has(keyOf(caseId));
}

export function getDecidedModerationCase(caseId) {
  return decided.get(keyOf(caseId)) || null;
}

export function getDecidedModerationCases() {
  const seen = new Set();
  return [...decided.values()].filter((row) => {
    const id = keyOf(row?.caseId);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function saveDecidedModerationCase(row) {
  const archiveKey = keyOf(row?.caseId);
  const queueKey = keyOf(row?.queueCaseId);
  if (!archiveKey && !queueKey) return null;
  // Index by archive ID (list/detail) and queue ID (remove from Queue after handoff).
  if (archiveKey) decided.set(archiveKey, row);
  if (queueKey && queueKey !== archiveKey) decided.set(queueKey, row);
  return row;
}

export function applyQueueSession(row) {
  if (!row?.caseId || isModerationCaseDecided(row.caseId)) return null;
  const patch = getModerationCaseOverride(row.caseId);
  if (!patch) return row;
  const supportTeam = patch.supportTeam || patch.assignedTo || row.supportTeam;
  const isAssigned =
    supportTeam && String(supportTeam).trim().toLowerCase() !== 'unassigned';
  let status = patch.status || row.status;
  if (status === 'Under Review') status = 'In Progress';
  // Assigned ↔ In Progress; unassigned ↔ New (until Decided).
  if (status !== 'Decided') {
    if (isAssigned && (!status || status === 'New')) {
      status = 'In Progress';
    } else if (!isAssigned && status === 'In Progress') {
      status = 'New';
    }
  }
  return {
    ...row,
    supportTeam,
    assignedTo: supportTeam,
    status,
    updated: patch.lastUpdated || row.updated,
    lastUpdated: patch.lastUpdated || row.lastUpdated,
  };
}
